import React, { useEffect, useState, useRef } from 'react';
import { injectIntl } from 'react-intl';
import Loadable from 'react-loadable';
import { cleanFieldProps } from '../../lib/ep.js';

import { SquaredButton } from './ActionButton.js';
import FieldWrapper from './FieldWrapper';
import FloatingLayer from './FloatingLayer';
import ButtonCollection from './ButtonCollection.js';
import Tooltip from './Tooltip.js';

const Wrapper = (props) => {
	const zoomable = props?.zoomable ?? false;
	
	const closeLayer = () => {
		props.setZoomed(false);
	};

	return (
		<>
			{props.children}

			{(zoomable && (props?.zoomed ?? false)) && (
				<FloatingLayer _class={{ size:'fullscreen', other:'zoomed_field' }} backButton={() => closeLayer()}>
					<div class="without_actions">
						{props.children}
					</div>
				</FloatingLayer>
			)}
		</>
	)
};

const EmojiPicker = Loadable({
	loader:() => import('./EmojiPicker'),
	loading:() => <div>Loading...</div>,
});

const TextArea = (props) => {
	const name = props?.name ?? false;
	if(name === false) return null;

	const refs = useRef({});

	const [ chars_left, setCharsLeft ] = useState(false);
	const [ has_many_chars, setHasManyChars ] = useState(false);
	const [ has_many_chars_min, setHasManyCharsMin ] = useState(false);
	const [ value, setValue ] = useState(props?.value ?? ((props.model != undefined && props.model[name] != null) ? props.model[name] : ''));
	const [ zoomed, setZoomed ] = useState(false);

	const _focus = props?._focus ?? false;
	const max_length = props?.max_length ?? null;
	const min_length = props?.min_length ?? null;
	const limit_max_length = props?.limit_max_length ?? true;
	const has_emojis = props?.has_emojis ?? false;
	const disabled = props?.disabled ?? false;
	const is_monospaced = props?.is_monospaced ?? false;
	const zoomable = props?.zoomable ?? false;
	const _placeholder = props?.placeholder ?? false;
	const autotranslate_placeholder = props?.autotranslate_placeholder ?? true;
	const placeholder = (_placeholder !== false) ? ((autotranslate_placeholder) ? props.intl.formatMessage({id:_placeholder}) : _placeholder) : null;

	const inputRef = (_ref) => {
		refs.current['element'] = _ref;
		if(props.inputRef !== undefined) props.inputRef(_ref);
	};

	//When the input change reflect the change
	const change = (e, max_length, min_length) => {
		if(max_length || min_length) typeText(e, max_length, min_length);
		//Call the passed callback
		if(props.change != undefined) props.change(name, e.target.value);
		setValue(e.target.value);
	};

	const focus = (e) => {
		if(refs.current['emojipicker'] != null) {
			refs.current['emojipicker'].close();
		}

		if(props.onFocus != undefined) props.onFocus(e);
	};

	useEffect(() => {
		if(_focus) refs.current['element'].focus();
	}, []);

	useEffect(() => {
		setValue(props?.value ?? ((props.model != undefined) ? props.model[name] : ''));
	}, [ props.value, props.model ]);

	const typeText = (e, max_length, min_length) => {
		if(!max_length && !min_length) return;

		if(max_length) {
			let chars_left = max_length - e.target.value.length;
			setHasManyChars((chars_left < 0) ? true : false);
			setCharsLeft(chars_left);
		}

		if(min_length) {
			let chars_pending = e.target.value.length - min_length;
			setHasManyCharsMin((chars_pending > 0) ? true : false);
		}
	};

	const concatText = (new_text) => {
		const box = $(refs.current['element']);
		const position_focus = box.prop("selectionStart");
		const position_start = 0;
		const position_end = box.val().length;

		let text = box.val();
		text = text.substr(position_start, position_focus) + new_text + text.substr(position_focus, position_end);

		change({ target:{ value:text } }, props.max_length);
	};

	let field_class = ['common_form_item'];
	if(disabled) field_class.push('disabledField');
	if(is_monospaced) field_class.push('is_monospaced');

	const wrapper_props = cleanFieldProps(props);

	const has_min_char_limit = (min_length && !has_many_chars_min) ? true : false;
	const has_max_char_limit = (max_length) ? true : false;
	const has_char_limits = (has_min_char_limit || has_max_char_limit) ? true : false;

	return (
		<Wrapper zoomable={zoomable} zoomed={zoomed} setZoomed={setZoomed}>
			<FieldWrapper
				{...wrapper_props}
				type='textarea'
				is_zoomable={zoomable}
			>
				<textarea
					ref={inputRef}
					dir="auto"
					maxLength={(limit_max_length && max_length) ? max_length : null}
					onFocus={focus}
					disabled={(disabled) ? 'disabled' : null}
					onKeyPress={(e) => typeText(e, max_length, min_length)}
					onChange={(e) => change(e, max_length, min_length)}
					class={field_class.join(" ")}
					style={props.style}
					value={value}
					placeholder={placeholder}
					aria-label={name}
					lang={props.lang}
				/>

				{(has_char_limits) && (
					<div class="gui_charcounter_c">
						{(has_min_char_limit) && (
							<div class={'is_monospaced gui_charcounter whiteColor redBg' + ((max_length) ? ' with_max_chars' : '')}>
								{props.intl.formatMessage({ id:"Min" })}: {min_length}
							</div>
						)}

						{(has_max_char_limit) && (
							<div class={'is_monospaced gui_charcounter whiteColor' + ((has_many_chars) ? ' redBg' : '')}>
								{(chars_left !== false) ? chars_left : max_length}
							</div>
						)}
					</div>
				)}

				{(has_emojis) && (
					<EmojiPicker
						ref={(_ref) => { refs.current['emojipicker'] = _ref; }}
						onEmojiPick={concatText}
					/>
				)}

				{(zoomable) && (
					<ButtonCollection _class={{ other:'drink_me' }}>
						<SquaredButton
							onClick={() => setZoomed(!zoomed)}
							icon={(!zoomed) ? 'knk-cookie' : 'knk-developers'}
							tooltip={<Tooltip with_icon={false}>{(!zoomed) ? "Eat me" : "Drink me"}</Tooltip>}
							aria-label="Toggle zoomed view"
						/>
					</ButtonCollection>
				)}
			</FieldWrapper>
		</Wrapper>
	);
}

export default injectIntl(TextArea);